import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Bell, CheckCircle2, Loader2, XCircle } from 'lucide-react'

export default function BrainEmprendedorInvitations() {
  const [data, setData] = useState({ invitations: [], unreadCount: 0 })
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [actionState, setActionState] = useState({ id: null, action: '', error: '' })

  const loadInvitations = async (signal) => {
    const response = await fetch('/api/brain/invitations', { credentials: 'include', signal })
    if (!response.ok) {
      throw new Error('No se pudieron cargar las invitaciones.')
    }

    return response.json()
  }

  useEffect(() => {
    let activeController = new AbortController()

    const load = async (preserveLoading = false) => {
      try {
        if (!preserveLoading) setLoading(true)
        setError('')
        setData(await loadInvitations(activeController.signal))
      } catch (err) {
        if (err.name !== 'AbortError') {
          setError(err.message || 'No se pudieron cargar las invitaciones.')
        }
      } finally {
        if (!activeController.signal.aborted) {
          setLoading(false)
        }
      }
    }

    load()

    const refresh = () => {
      activeController.abort()
      activeController = new AbortController()
      load(true)
    }

    const intervalId = window.setInterval(() => {
      if (document.visibilityState === 'visible') {
        refresh()
      }
    }, 15000)

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        refresh()
      }
    }

    window.addEventListener('focus', refresh)
    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      activeController.abort()
      window.clearInterval(intervalId)
      window.removeEventListener('focus', refresh)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [])

  const respond = async (invitationId, action) => {
    setActionState({ id: invitationId, action, error: '' })

    try {
      const response = await fetch(`/api/brain/invitations/${invitationId}/respond`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ action })
      })
      const payload = await response.json().catch(() => null)
      if (!response.ok) {
        throw new Error(payload?.message || 'No se pudo responder la invitacion.')
      }

      setData((current) => ({
        unreadCount: Math.max(0, (current.unreadCount || 0) - 1),
        invitations: (current.invitations || []).map((invitation) => (
          invitation.id === invitationId
            ? { ...invitation, status: payload.status, statusLabel: payload.statusLabel, roomId: payload.roomId, roomName: payload.roomName }
            : invitation
        ))
      }))
      setActionState({ id: null, action: '', error: '' })
    } catch (err) {
      setActionState({ id: invitationId, action, error: err.message || 'No se pudo responder la invitacion.' })
    }
  }

  if (loading) {
    return <div className="min-h-[60vh] flex items-center justify-center text-slate-600"><Loader2 className="w-6 h-6 animate-spin mr-2" />Cargando invitaciones...</div>
  }

  if (error) {
    return <div className="rounded-3xl border border-rose-200 bg-rose-50 p-6 text-rose-700">{error}</div>
  }

  return (
    <div className="space-y-6">
      <section className="rounded-[28px] bg-slate-950 text-slate-100 p-8 md:p-10">
        <div className="inline-flex items-center gap-2 rounded-full border border-amber-300/30 px-3 py-1 text-xs uppercase tracking-wide text-amber-200">
          <Bell className="w-3.5 h-3.5" />
          BrainEmprendedor
        </div>
        <h1 className="mt-5 font-display text-4xl text-white">Invitaciones del equipo</h1>
        <p className="mt-4 max-w-2xl text-slate-300">
          Revisa las rooms a las que te invitaron y decide si quieres entrar al espacio de trabajo colaborativo.
        </p>
      </section>

      <section className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
        <div className="flex items-center justify-between gap-3">
          <div>
            <h2 className="text-2xl font-semibold text-slate-900">Bandeja de invitaciones</h2>
            <p className="mt-1 text-sm text-slate-500">{data.unreadCount} pendientes por responder.</p>
          </div>
          <Link to="/brain-emprendedor/dashboard" className="text-sm font-medium text-slate-600 hover:text-slate-900">
            Volver al dashboard
          </Link>
        </div>

        <div className="mt-6 space-y-4">
          {(data.invitations || []).length === 0 && (
            <div className="rounded-2xl bg-slate-50 p-5 text-sm text-slate-600">
              No tienes invitaciones de BrainEmprendedor por ahora.
            </div>
          )}

          {(data.invitations || []).map((invitation) => {
            const isPending = invitation.status === 'Pending'
            const isAccepting = actionState.id === invitation.id && actionState.action === 'accept'
            const isRejecting = actionState.id === invitation.id && actionState.action === 'reject'

            return (
              <article key={invitation.id} className="rounded-2xl border border-slate-200 p-5">
                <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                  <div>
                    <div className="flex flex-wrap items-center gap-2">
                      <h3 className="text-lg font-semibold text-slate-900">{invitation.roomName}</h3>
                      <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${
                        invitation.status === 'Accepted'
                          ? 'bg-emerald-100 text-emerald-700'
                          : invitation.status === 'Rejected'
                            ? 'bg-rose-100 text-rose-700'
                            : 'bg-amber-100 text-amber-700'
                      }`}>
                        {invitation.statusLabel}
                      </span>
                    </div>
                    <p className="mt-2 text-sm text-slate-600">Invitada por {invitation.senderName}</p>
                    <p className="mt-1 text-xs text-slate-500">{invitation.createdAtLabel}</p>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    {isPending && (
                      <>
                        <button
                          type="button"
                          onClick={() => respond(invitation.id, 'accept')}
                          disabled={isAccepting || isRejecting}
                          className="inline-flex items-center gap-2 rounded-xl bg-emerald-600 px-4 py-3 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-60"
                        >
                          <CheckCircle2 className="w-4 h-4" />
                          {isAccepting ? 'Aceptando...' : 'Aceptar'}
                        </button>
                        <button
                          type="button"
                          onClick={() => respond(invitation.id, 'reject')}
                          disabled={isAccepting || isRejecting}
                          className="inline-flex items-center gap-2 rounded-xl border border-rose-200 px-4 py-3 text-sm font-semibold text-rose-700 hover:bg-rose-50 disabled:opacity-60"
                        >
                          <XCircle className="w-4 h-4" />
                          {isRejecting ? 'Rechazando...' : 'Rechazar'}
                        </button>
                      </>
                    )}
                    {invitation.status === 'Accepted' && (
                      <Link to={`/brain-emprendedor/rooms/${invitation.roomId}`} className="inline-flex items-center gap-2 rounded-xl bg-slate-900 px-4 py-3 text-sm font-semibold text-white hover:bg-slate-800">
                        Entrar al room
                      </Link>
                    )}
                  </div>
                </div>
                {actionState.id === invitation.id && actionState.error && (
                  <div className="mt-4 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                    {actionState.error}
                  </div>
                )}
              </article>
            )
          })}
        </div>
      </section>
    </div>
  )
}
